import { isProxy, toRaw } from "vue";
import { DateTime } from "luxon";

function cloneValue<TValue>(value: TValue, seen: WeakMap<object, unknown>): TValue {
  if (value == null || typeof value !== "object") {
    return value;
  }

  const rawValue = isProxy(value) ? toRaw(value) : value;

  if (rawValue instanceof Date) {
    return new Date(rawValue.getTime()) as TValue;
  }

  if (DateTime.isDateTime(rawValue)) {
    // Luxon DateTime values are immutable, so retaining the instance is a stable snapshot.
    return rawValue as TValue;
  }

  if (!Array.isArray(rawValue) && Object.getPrototypeOf(rawValue) !== Object.prototype) {
    throw new TypeError(
      "Validation baselines only support arrays, plain objects, Date, and Luxon DateTime values",
    );
  }

  if (seen.has(rawValue)) {
    throw new TypeError("Cyclic validation values are not supported");
  }

  if (Array.isArray(rawValue)) {
    const clone: unknown[] = [];
    seen.set(rawValue, clone);
    rawValue.forEach((entry) => {
      clone.push(cloneValue(entry, seen));
    });
    return clone as TValue;
  }

  const clone: Record<string, unknown> = {};
  seen.set(rawValue, clone);
  Object.entries(rawValue).forEach(([key, entry]) => {
    clone[key] = cloneValue(entry, seen);
  });
  return clone as TValue;
}

export function cloneValidationValue<TValue>(value: TValue): TValue {
  return cloneValue(value, new WeakMap());
}
